import React, { useState, useCallback } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import SettingsIcon from '@mui/icons-material/Settings'
import TagIcon from '@mui/icons-material/Tag'
import ApiIcon from '@mui/icons-material/Api'
import { FC, useCQuery, invalidateQuery } from '../../Services'

const API_BASE = typeof window !== 'undefined' && window._env_?.API_BASE_URL
  ? window._env_.API_BASE_URL
  : 'http://localhost:3000'

export const Settings = () => {
  const { data: settings, isLoading } = useCQuery('appSettings')
  const [toggling, setToggling] = useState(false)

  const handleToggleHashAS400 = useCallback(async () => {
    setToggling(true)
    try {
      const newValue = !settings?.hashAS400
      await FC.service('app-settings').patch('global', { hashAS400: newValue })
      invalidateQuery('appSettings')
      window.growl.show({
        severity: 'success',
        summary: 'Successo',
        detail: newValue
          ? 'Hash AS400 attivato. I file inviati includeranno l\'hash e lo stato passerà a "Inviato" in attesa di conferma da AS400.'
          : 'Hash AS400 disattivato. I file inviati passeranno direttamente allo stato "Consegnato".'
      })
    } catch (err) {
      window.growl.show({
        severity: 'error',
        summary: 'Errore',
        detail: err?.message || 'Impossibile modificare l\'impostazione'
      })
    }
    setToggling(false)
  }, [settings?.hashAS400])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Stack alignItems='center' spacing={2}>
          <CircularProgress size={40} />
          <Typography variant='body2' color='text.secondary'>
            Caricamento...
          </Typography>
        </Stack>
      </Box>
    )
  }

  const isHashActive = settings?.hashAS400

  return (
    <Box sx={{ mx: 'auto', width: '94%', maxWidth: 1200, pb: 5 }}>
      <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 3, mt: 1 }}>
        <SettingsIcon color='primary' sx={{ fontSize: 28 }} />
        <Typography variant='h5' fontWeight={700} color='text.primary'>
          Impostazioni
        </Typography>
      </Stack>

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Stack direction='row' alignItems='center' spacing={2}>
            <TagIcon sx={{ fontSize: 22, color: isHashActive ? 'primary.main' : 'text.disabled' }} />
            <Typography variant='h6' fontWeight={600}>
              Hash AS400
            </Typography>
            <Chip
              label={isHashActive ? 'ATTIVO' : 'NON ATTIVO'}
              color={isHashActive ? 'primary' : 'default'}
              size='small'
              sx={{ fontWeight: 600 }}
            />
          </Stack>
          <Stack direction='row' alignItems='center' spacing={1}>
            {toggling && <CircularProgress size={20} />}
            <Switch
              checked={!!isHashActive}
              onChange={handleToggleHashAS400}
              disabled={toggling}
              color='primary'
            />
          </Stack>
        </Stack>

        {isHashActive && settings?.updatedAt && (
          <Alert severity='info' sx={{ mt: 2.5 }} variant='outlined'>
            Attivato il <strong>{new Date(settings.updatedAt).toLocaleString('it-IT')}</strong>
            {settings.updatedBy ? <> da <strong>{settings.updatedBy}</strong></> : ''}
          </Alert>
        )}

        {!isHashActive && settings?.updatedAt && (
          <Alert severity='success' sx={{ mt: 2.5 }} variant='outlined'>
            Ultima modifica: <strong>{new Date(settings.updatedAt).toLocaleString('it-IT')}</strong>
            {settings.updatedBy ? <> da <strong>{settings.updatedBy}</strong></> : ''}
          </Alert>
        )}

        <Divider sx={{ my: 2.5 }} />

        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.8 }}>
          Quando l'impostazione Hash AS400 è <strong>attiva</strong>:
        </Typography>
        <Box component='ul' sx={{ mt: 1, pl: 2, '& li': { mb: 0.5 } }}>
          <Typography component='li' variant='body2' color='text.secondary' sx={{ lineHeight: 1.8 }}>
            Il <code>fileHash</code> originale (SHA-256 del file letto dal client) viene aggiunto in coda al file prima dell'invio FTP, come identificatore univoco del processo.
          </Typography>
          <Typography component='li' variant='body2' color='text.secondary' sx={{ lineHeight: 1.8 }}>
            Per file <strong>non XML</strong> (AMA, SBR, MIR, WSP): <code>siapcrs: {'<hash>'}</code>
          </Typography>
          <Typography component='li' variant='body2' color='text.secondary' sx={{ lineHeight: 1.8 }}>
            Per file <strong>XML</strong> (NTV, PIC): <code>{'<siapcrs><hash></siapcrs>'}</code> inserito{' '}
            <strong>dentro l&apos;elemento root</strong> del documento (resta un solo root XML valido).
          </Typography>
          <Typography component='li' variant='body2' color='text.secondary' sx={{ lineHeight: 1.8 }}>
            Lo stato del processo passa a <strong>"Inviato"</strong> anziché "Consegnato" dopo l'upload FTP.
          </Typography>
          <Typography component='li' variant='body2' color='text.secondary' sx={{ lineHeight: 1.8 }}>
            AS400 conferma la ricezione tramite l'endpoint <code>/hash-confirm</code>, portando a <strong>"Consegnato"</strong> tutti i processi con lo stesso hash in stato &quot;Inviato&quot;.
          </Typography>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.8 }}>
          Quando l'impostazione è <strong>disattiva</strong>, il programma si comporta normalmente:
          i file vengono inviati senza hash e lo stato passa direttamente a "Consegnato" dopo l'upload FTP.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mt: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 2 }}>
          <ApiIcon color='primary' />
          <Typography variant='h6' fontWeight={600}>
            API conferma ricezione (AS400)
          </Typography>
        </Stack>

        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5, lineHeight: 1.8 }}>
          L&apos;endpoint accetta JWT (<code>Authorization: Bearer …</code>) oppure token machine con strategia{' '}
          <code>client</code>. Per AS400: login con <code>strategy: &quot;as400&quot;</code> (username e password come il login
          web); la risposta contiene lo stesso <code>accessToken</code> JWT da usare come Bearer. Con quel flusso, errori di
          autenticazione su <code>/hash-confirm</code> (token assente/scaduto/non valido) rispondono HTTP <strong>400</strong>{' '}
          invece di 401.
        </Typography>

        <Typography variant='subtitle2' fontWeight={700} sx={{ mt: 2, mb: 1 }}>
          Come ottenere il JWT
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1, lineHeight: 1.8 }}>
          Effettuare una richiesta <strong>POST</strong> al servizio di autenticazione (REST). La risposta contiene&nbsp;<code>accessToken</code>: stringa JWT da inviare in tutte le chiamate successive.
        </Typography>
        <Box
          component='pre'
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: 'grey.900',
            color: 'grey.100',
            fontSize: 12,
            overflow: 'auto',
            fontFamily: 'ui-monospace, monospace',
            lineHeight: 1.5
          }}
        >
{`POST ${API_BASE}/authentication
Content-Type: application/json

{
  "strategy": "local",
  "username": "<username utente CRSLink>",
  "password": "<password>"
}`}
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5, lineHeight: 1.8 }}>
          Risposta tipica: <code>{`{ "accessToken": "eyJ...", "user": { ... } }`}</code>
        </Typography>

        <Typography variant='subtitle2' fontWeight={700} sx={{ mt: 2, mb: 1 }}>
          Login AS400 (<code>strategy: as400</code>)
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1, lineHeight: 1.8 }}>
          Stessi campi della strategia <code>local</code> (username e password utente CRSLink). In caso di credenziali errate
          la risposta è HTTP <strong>400</strong> (non 401).
        </Typography>
        <Box
          component='pre'
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: 'grey.900',
            color: 'grey.100',
            fontSize: 12,
            overflow: 'auto',
            fontFamily: 'ui-monospace, monospace',
            lineHeight: 1.5
          }}
        >
{`POST ${API_BASE}/authentication
Content-Type: application/json

{
  "strategy": "as400",
  "username": "<username utente CRSLink>",
  "password": "<password>"
}`}
        </Box>

        <Typography variant='subtitle2' fontWeight={700} sx={{ mt: 2.5, mb: 1 }}>
          Conferma hash — metodo, intestazioni e body
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1, lineHeight: 1.8 }}>
          Il valore <code>hash</code> nel body è lo stesso identificatore presente in coda al file (e coincide con <code>fileHash</code> sul processo — SHA-256 del file originale lato client).
        </Typography>
        <Box
          component='pre'
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: 'grey.900',
            color: 'grey.100',
            fontSize: 12,
            overflow: 'auto',
            fontFamily: 'ui-monospace, monospace',
            lineHeight: 1.5
          }}
        >
{`POST ${API_BASE}/hash-confirm
Authorization: Bearer <accessToken>
Content-Type: application/json

{
  "hash": "abc123..."
}`}
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5, lineHeight: 1.8 }}>
          Risposta JSON in caso di successo (HTTP 201):{' '}
          <code>{`{ "statusCode": 201, "updatedCount": 1, "processIds": ["..."], "processId": "...", "fileNames": ["..."], "fileName": "...", "message": "..." }`}</code>
          . Se più processi con lo stesso hash sono in stato &quot;inviato&quot;, vengono tutti portati a &quot;consegnato&quot;.
          Se manca o non è valido il campo <code>hash</code>, errore HTTP 400; se non esiste un processo in stato
          &quot;inviato&quot; con quel hash, HTTP 404 con corpo errore Feathers (<code>name</code>, <code>message</code>,{' '}
          <code>code</code>). Se il Bearer è un JWT ottenuto con <code>strategy: &quot;as400&quot;</code>, errori di
          autenticazione sul token sono HTTP 400; con JWT da login <code>local</code> resta 401.
        </Typography>
      </Paper>
    </Box>
  )
}
